/**
 * Sentry Configuration for Frontend Error Monitoring
 *
 * Sentry provides real-time error tracking and performance monitoring
 * for React applications.
 *
 * Setup:
 * 1. Sign up at https://sentry.io
 * 2. Create a new project (React)
 * 3. Get your DSN from Project Settings > Client Keys
 * 4. Add REACT_APP_SENTRY_DSN to your environment variables
 *
 * Documentation: https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error monitoring for React
 */
export function initializeSentry() {
  // Only initialize if DSN is provided
  if (!process.env.REACT_APP_SENTRY_DSN) {
    if (process.env.NODE_ENV === 'production') {
      console.warn('⚠️  REACT_APP_SENTRY_DSN not configured - error monitoring disabled');
    }
    return false;
  }

  try {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,

      // Environment (production, staging, development)
      environment: process.env.NODE_ENV || 'development',

      // Release version for tracking
      release: process.env.REACT_APP_SENTRY_RELEASE || `snaptura-frontend@${process.env.REACT_APP_VERSION || '1.0.0'}`,

      // Performance Monitoring
      integrations: [
        // Browser tracing for performance monitoring with React Router v6
        Sentry.reactRouterV6BrowserTracingIntegration({
          // useEffect is required for React Router v6 instrumentation
          useEffect: require('react').useEffect,
          useLocation: require('react-router-dom').useLocation,
          useNavigationType: require('react-router-dom').useNavigationType,
          createRoutesFromChildren: require('react-router-dom').createRoutesFromChildren,
          matchRoutes: require('react-router-dom').matchRoutes,
        }),

        // Replay integration for session replay
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Performance Monitoring sample rate
      tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

      // Session Replay sample rate
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Before send hook - modify or filter events before sending
      beforeSend(event, hint) {
        // Filter out certain errors in development
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Sentry Event:', event.exception?.values?.[0]?.type);
        }

        // Don't send errors in test environment
        if (process.env.NODE_ENV === 'test') {
          return null;
        }

        // Filter sensitive data from errors
        if (event.request) {
          // Remove sensitive headers
          if (event.request.headers) {
            delete event.request.headers['authorization'];
            delete event.request.headers['cookie'];
          }
        }

        // Filter out specific error types
        const errorMessage = hint.originalException?.message || '';
        const ignoredErrors = [
          'ResizeObserver loop limit exceeded',
          'Non-Error promise rejection captured',
          'Network Error',
          'timeout of 0ms exceeded',
        ];

        if (ignoredErrors.some(ignored => errorMessage.includes(ignored))) {
          return null; // Don't send to Sentry
        }

        // Filter out errors from browser extensions
        if (event.exception?.values?.[0]?.stacktrace?.frames) {
          const frames = event.exception.values[0].stacktrace.frames;
          if (frames.some(frame => frame.filename?.includes('chrome-extension://'))) {
            return null;
          }
        }

        return event;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome.runtime',
        // Random plugins/extensions
        'Can\'t find variable: ZiteReader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        // Facebook errors
        'fb_xd_fragment',
        // Network errors
        'Network request failed',
        'NetworkError',
        // AbortController errors
        'AbortError',
      ],

      // Denylist of URLs to ignore
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        // Local files
        /^file:\/\//i,
      ],
    });

    console.log('✅ Sentry error monitoring initialized (Frontend)');
    console.log(`   Environment: ${process.env.NODE_ENV}`);
    console.log(`   Traces Sample Rate: ${process.env.NODE_ENV === 'production' ? '10%' : '100%'}`);

    return true;
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error.message);
    return false;
  }
}

/**
 * Manually capture an exception
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    return;
  }

  Sentry.withScope((scope) => {
    // Add context
    if (context.user) {
      scope.setUser({
        id: context.user.id,
        email: context.user.email,
      });
    }

    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context.extra) {
      scope.setContext('extra', context.extra);
    }

    Sentry.captureException(error);
  });
}

/**
 * Manually capture a message
 * @param {string} message - Message to capture
 * @param {string} level - Severity level (fatal, error, warning, log, info, debug)
 * @param {Object} context - Additional context
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    return;
  }

  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context.extra) {
      scope.setContext('extra', context.extra);
    }

    Sentry.captureMessage(message, level);
  });
}

/**
 * Set user context for error tracking
 * @param {Object} user - User object
 */
export function setUser(user) {
  if (!process.env.REACT_APP_SENTRY_DSN || !user) {
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

/**
 * Clear user context
 */
export function clearUser() {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    return;
  }

  Sentry.setUser(null);
}

/**
 * Create an error boundary component
 * Wrap your app or specific components with this
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

export default Sentry;
